import { useState, useEffect } from 'react';
import ImageCleanerWidget from './ImageCleanerWidget';
import { Image, Upload } from 'lucide-react';
import type { User } from '@supabase/supabase-js';

interface ImageCleanerWidgetClientProps {
  user: User | null;
  isGridLayout?: boolean;
}

/**
 * Client-only wrapper for ImageCleanerWidget with hydration-safe rendering
 * HYDRATION FIX: Always renders the same content on server and first client render
 * Only shows the actual component after hydration completes
 */
export default function ImageCleanerWidgetClient(props: ImageCleanerWidgetClientProps) {
  // HYDRATION FIX: Track if component has hydrated
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    // Render a static version during SSR that looks like the actual component
    return (
      <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-soft border border-neutral-200 dark:border-neutral-800 overflow-hidden h-full flex flex-col">
        {/* Header */}
        <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6 border-b border-neutral-100 dark:border-neutral-800">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-800">
              <Image className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                Clean Gemini Images
              </h2>
              <p className="text-neutral-500 dark:text-neutral-400 text-sm">
                Remove watermarks and strip metadata
              </p>
            </div>
          </div>
        </div>

        {/* Upload Area */}
        <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6 flex-1">
          <div className="w-full min-h-[180px] rounded-xl bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 flex flex-col items-center justify-center p-6 text-center">
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-800 mb-4">
              <Upload className="w-8 h-8 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-sm font-medium text-neutral-900 dark:text-white mb-1">
              Drop your image here
            </h3>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
              or click to browse files
            </p>
            <div className="flex items-center gap-3 text-xs text-neutral-600 dark:text-neutral-400">
              <span className="flex items-center gap-1">
                <Image size={12} />
                JPEG, PNG, WebP
              </span>
              <span>•</span>
              <span>Max 20MB</span>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="px-5 sm:px-6 lg:px-8 py-5 sm:py-6 bg-neutral-50 dark:bg-neutral-800/30 border-t border-neutral-100 dark:border-neutral-800 mt-auto">
          <button
            disabled
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 rounded-xl font-medium cursor-not-allowed"
          >
            Remove Watermark & Download
          </button>
          <p className="text-center text-xs text-neutral-500 dark:text-neutral-400 mt-3">
            All processing happens locally in your browser. Your image never leaves your device.
          </p>
        </div>
      </div>
    );
  }

  // Render the actual component on the client
  return <ImageCleanerWidget {...props} />;
}
